import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import ArrowButton from "../../components/ArrowButton/ArrowButton";
import { PageSeo } from "../../seo/Seo";
import "../../components/Header/Header.css";
import "./ParceiroPedagogico.css";
import logoOficial from "../../assets/logos/logo-oficial-calriz.png";
import seloPartner from "../../assets/logos/selo-partner.png";

// Fotos de case: coloque os arquivos em public/parceiro/ com estes nomes.
const asset = (name: string) => `${process.env.PUBLIC_URL}/parceiro/${name}`;

type Tab = "basica" | "superior";

const NAV_LINKS = [
  { id: "atuacao", label: "Como atuamos" },
  { id: "segmentos", label: "Educação" },
  { id: "aluno-tutor", label: "Aluno Tutor" },
  { id: "cases", label: "Cases" },
];

// Anima a contagem de 0 até o valor final quando entra na viewport.
function CountUp({
  end,
  prefix = "",
  suffix = "",
  duration = 1600,
}: {
  end: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
}) {
  const ref = useRef<HTMLElement>(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReduced) {
      setValue(end);
      return;
    }

    let raf = 0;
    let start = 0;
    const animate = (t: number) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(end * eased));
      if (p < 1) raf = requestAnimationFrame(animate);
    };

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            raf = requestAnimationFrame(animate);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(el);

    return () => {
      cancelAnimationFrame(raf);
      obs.disconnect();
    };
  }, [end, duration]);

  return (
    <strong ref={ref}>
      {prefix}
      {value.toLocaleString("pt-BR")}
      {suffix}
    </strong>
  );
}

const TABS: Record<
  Tab,
  { eyebrow: string; title: string; text: string; items: string[] }
> = {
  basica: {
    eyebrow: "Educação Básica",
    title: "Transformação digital conectada à realidade das escolas",
    text: "A Calriz atua com redes municipais e estaduais de ensino em projetos que desenvolvem competências digitais, fortalecem o trabalho dos educadores e estimulam o protagonismo dos estudantes.",
    items: [
      "Formação de professores e equipes pedagógicas",
      "Google for Education aplicado ao cotidiano escolar",
      "Gemini para planejamento e práticas pedagógicas",
      "Programas de formação de multiplicadores",
      "Metodologias ativas e inovação educacional",
      "Programa Aluno Tutor de Tecnologia",
    ],
  },
  // TODO: revisar a copy do Ensino Superior (o HTML original não a trazia).
  superior: {
    eyebrow: "Ensino Superior",
    title: "Inovação acadêmica com tecnologia e inteligência artificial",
    text: "A Calriz apoia instituições de Ensino Superior no uso pedagógico do Google Workspace, do Gemini e de ambientes como o Moodle, fortalecendo docentes, pesquisadores e equipes acadêmicas.",
    items: [
      "Formação de docentes e equipes acadêmicas",
      "Google Workspace for Education no contexto universitário",
      "Gemini aplicado ao ensino e à pesquisa",
      "Jornadas de aprendizagem no Moodle",
      "Inteligência artificial no Ensino Superior",
      "Programas em parceria com a RNP",
    ],
  },
};

export default function ParceiroPedagogico() {
  const pageRef = useRef<HTMLDivElement>(null);
  const [navOpen, setNavOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("basica");
  const [videoOpen, setVideoOpen] = useState(false);
  const [active, setActive] = useState<string>("");
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia("(max-width: 640px)").matches
  );

  // O selo de parceiro não é renderizado no mobile — assim a imagem também
  // não é baixada, e não só escondida por CSS.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Revela os elementos .reveal ao entrarem na viewport (substitui o script.js).
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const els = Array.from(root.querySelectorAll<HTMLElement>(".reveal"));
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // Destaca no menu a seção visível na viewport (scrollspy).
  // "contato" entra na lista para esconder o botão flutuante nessa seção.
  useEffect(() => {
    const ids = [
      "atuacao",
      "segmentos",
      "aluno-tutor",
      "cases",
      "experiencias",
      "contato",
    ];
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    const obs = new IntersectionObserver(
      (entries) => {
        entries
          .filter((e) => e.isIntersecting)
          .forEach((e) => setActive(e.target.id));
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    sections.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // Fecha o modal de vídeo com Esc e trava o scroll da página atrás dele.
  useEffect(() => {
    if (!videoOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setVideoOpen(false);
    window.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [videoOpen]);

  // Drawer mobile: fecha com Esc e trava o scroll da página atrás dele.
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setNavOpen(false);
    window.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [navOpen]);

  const panel = TABS[tab];

  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<null | "success" | "error">(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      name: String(data.get("name") || ""),
      email: String(data.get("email") || ""),
      institution: String(data.get("institution") || ""),
      interest: String(data.get("interest") || ""),
      message: String(data.get("message") || ""),
    };

    setSubmitting(true);
    setStatus(null);
    try {
      const res = await fetch(
        "https://backend.calriz.com/api/parceiro-pedagogico",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (res.ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
      }
    } catch (err) {
      console.error("Erro ao enviar formulário:", err);
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="parceiro-page cz-scope" ref={pageRef}>
      <PageSeo page="parceiro_pedagogico" path="/parceiro-pedagogico" />
      <main>
        <header className="cz-header cz-scope">
          <div className="cz-header__inner">
            <Link to="/" className="cz-header__logo" aria-label="Calriz - site principal">
              <img src={logoOficial} alt="Calriz" decoding="async" fetchPriority="high" />
            </Link>

            <nav className="cz-header__nav" aria-label="Principal">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  className={`cz-header__link${active === link.id ? " is-active" : ""}`}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            <ArrowButton href="#contato" variant="navy" size="sm" className="cz-header__cta">
              Fale conosco
            </ArrowButton>

            <button
              type="button"
              className="cz-header__burger"
              aria-label="Abrir menu"
              aria-expanded={navOpen}
              aria-controls="cz-drawer"
              onClick={() => setNavOpen(true)}
            >
              <Menu size={26} />
            </button>
          </div>
        </header>

        {navOpen && (
          <div className="cz-drawer-overlay cz-scope" onClick={() => setNavOpen(false)}>
            <div
              id="cz-drawer"
              className="cz-drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Menu"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="cz-drawer__close"
                aria-label="Fechar menu"
                onClick={() => setNavOpen(false)}
                autoFocus
              >
                <X size={26} />
              </button>
              <nav className="cz-drawer__main" aria-label="Principal">
                {NAV_LINKS.map((link) => (
                  <a key={link.id} href={`#${link.id}`} className="cz-drawer__link" onClick={() => setNavOpen(false)}>
                    {link.label}
                  </a>
                ))}
              </nav>
              <nav className="cz-drawer__utility" aria-label="Calriz">
                <Link to="/" className="cz-drawer__small-link">Site Calriz</Link>
                <Link to="/solucoes" className="cz-drawer__small-link">Soluções</Link>
                <Link to="/blog" className="cz-drawer__small-link">Blog</Link>
              </nav>
              <ArrowButton href="#contato" variant="pink" size="md" onClick={() => setNavOpen(false)}>
                Fale conosco
              </ArrowButton>
            </div>
          </div>
        )}

        <section className="hero" id="inicio">
          <div className="hero-orb orb-one"></div>
          <div className="hero-orb orb-two"></div>

          <div className="pp-container hero-grid">
            <div className="hero-copy reveal is-visible">
              <h1>
                Tecnologia que se transforma em <em>prática pedagógica</em>
              </h1>
              <p>
                Da Educação Básica ao Ensino Superior, a Calriz apoia redes
                públicas e instituições de ensino na construção de jornadas de
                formação continuada, letramento digital e uso pedagógico das
                tecnologias Google for Education e Gemini.
                <br />
                <br />
                Combinamos conhecimento técnico, experiência educacional e
                atividades práticas para que educadores, estudantes e equipes
                acadêmicas utilizem a tecnologia com mais autonomia, propósito e
                impacto.
              </p>
              <div className="hero-actions">
                <ArrowButton href="#contato" variant="pink" size="md" className="hero-cta">
                  Fale com nosso time de Educação
                </ArrowButton>
                <a className="button button-ghost" href="#cases">
                  Conheça nossos cases
                </a>
              </div>
            </div>

            {!isMobile && (
              <div className="hero-visual reveal is-visible">
                <div className="partner-mark">
                  <img
                    src={seloPartner}
                    alt="Google Cloud Latin America Partner of the Year 2024"
                    decoding="async"
                    fetchPriority="high"
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="section expertise-section" id="atuacao">
          <div className="brand-shapes corner-shapes" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>

          <div className="pp-container">
            <div className="section-heading centered reveal">
              <span className="eyebrow">Como a Calriz atua</span>
              <h2>Formação que conecta tecnologia, educação e impacto</h2>
              <p>
                Conheça as frentes que transformam ferramentas digitais em
                experiências de aprendizagem, colaboração e inovação.
              </p>
            </div>

            <div className="expertise-grid">
              {[
                ["01", "Formação continuada", "Jornadas formativas desenvolvidas para ampliar a fluência digital e incorporar novas ferramentas ao cotidiano educacional."],
                ["02", "Oficinas práticas e imersivas", "Experiências orientadas à aplicação da tecnologia no planejamento, na docência e na gestão."],
                ["03", "Inteligência artificial na educação", "Uso prático, ético e pedagógico do Gemini para apoiar educadores, docentes e equipes acadêmicas."],
                ["04", "Programas de multiplicadores", "Formação de profissionais capazes de disseminar conhecimento e fortalecer a cultura de inovação em suas redes."],
                ["05", "Protagonismo estudantil", "Iniciativas como o Programa Aluno Tutor, que prepara estudantes para atuarem como multiplicadores de tecnologia."],
                ["06", "Google for Education", "Formações voltadas ao uso estratégico das ferramentas Google para colaboração, produtividade e aprendizagem."],
              ].map(([n, title, text]) => (
                <article className="expertise-card reveal" key={n}>
                  <span className="card-number">{n}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section education-section" id="segmentos">
          <div className="pp-container education-layout">
            <div className="education-intro reveal">
              <span className="eyebrow">Educação Básica e Ensino Superior</span>
              <h2>Uma atuação conectada a cada realidade educacional</h2>
              <p>
                Selecione um segmento para conhecer as principais frentes de
                atuação pedagógica da Calriz.
              </p>
            </div>

            <div className="education-panel-col">
              <div className="tab-buttons" role="tablist" aria-label="Segmentos">
                <button
                  className={tab === "basica" ? "active" : ""}
                  type="button"
                  role="tab"
                  aria-selected={tab === "basica"}
                  aria-controls="education-panel"
                  onClick={() => setTab("basica")}
                >
                  Educação Básica
                </button>
                <button
                  className={tab === "superior" ? "active" : ""}
                  type="button"
                  role="tab"
                  aria-selected={tab === "superior"}
                  aria-controls="education-panel"
                  onClick={() => setTab("superior")}
                >
                  Ensino Superior
                </button>
              </div>

              <article
                className="education-panel reveal"
                id="education-panel"
                role="tabpanel"
                aria-live="polite"
              >
                <div className="panel-topline">
                  <span>{panel.eyebrow}</span>
                </div>
                <h3>{panel.title}</h3>
                <p>{panel.text}</p>
                <ul>
                  {panel.items.map((it) => (
                    <li key={it}>
                      <span>✓</span>
                      {it}
                    </li>
                  ))}
                </ul>
              </article>
            </div>
          </div>
        </section>

        <section className="section author-section" id="aluno-tutor">
          <div className="pp-container author-grid">
            <div className="author-media author-mosaic reveal">
              <img
                className="author-mosaic-main"
                src={asset("aluno-tut3.png")}
                alt="Estudantes participando de atividade do Programa Aluno Tutor"
                loading="lazy"
                decoding="async"
              />
              <img
                className="author-mosaic-top"
                src={asset("aluno-tut2.png")}
                alt="Alunos tutores apresentando durante o programa"
                loading="lazy"
                decoding="async"
              />
              <img
                className="author-mosaic-bottom"
                src={asset("aluno-tut1.png")}
                alt="Aluno tutor em apresentação do programa"
                loading="lazy"
                decoding="async"
              />
            </div>

            <div className="author-copy reveal">
              <h2>
                Aluno Tutor: um programa para formar protagonistas da
                transformação digital
              </h2>
              <p className="lead">
                Uma parceria entre Google e Calriz, o Programa Aluno Tutor prepara
                estudantes para utilizar o ecossistema Google for Education e
                outras ferramentas digitais com autonomia, segurança e propósito.
              </p>
              <p>
                Mais do que aprender a usar tecnologia, os participantes são
                preparados para compartilhar conhecimento, apoiar colegas e
                professores e atuar como multiplicadores de inovação em suas
                comunidades escolares.
              </p>
              <div className="author-highlight">
                <strong>
                  A Calriz formará a primeira turma de Alunos Tutores Indígenas.
                </strong>
              </div>
              <div className="pill-list">
                <span>Protagonismo e autonomia estudantil</span>
                <span>Formação de multiplicadores</span>
                <span>Inclusão e cidadania digital</span>
                <span>Aplicação em diferentes contextos</span>
              </div>
            </div>
          </div>
        </section>

        <section className="section bertioga-section" id="cases">
          <div className="pp-container">
            <div className="case-label-row reveal">
              <span className="eyebrow">Cases de destaque</span>
            </div>

            <div className="case-title-grid reveal">
              <div className="case-title">
              <h2>
                Bertioga: uma transformação educacional que conquistou
                reconhecimento internacional
              </h2>
              </div>
              <p className="case-description">
                A atuação da Calriz em Bertioga foi além da adoção de ferramentas.
                O projeto conectou formação continuada, uso pedagógico do Google
                for Education e do Gemini, desenvolvimento de competências
                digitais, protagonismo estudantil por meio do Programa Aluno Tutor
                e iniciativas voltadas à comunidade indígena Guarani.
                <br />
                <br />
                Como resultado, Bertioga consolidou-se como Município Referência
                Google for Education no Brasil e ganhou projeção internacional pelo
                uso da tecnologia na educação e pela experiência de alfabetização
                digital indígena na comemoração dos 10 anos do Google Sala de Aula
                em 2024. O município também foi apresentado como referência em
                evento oficial do Google dedicado à educação digital.
              </p>
            </div>

            <div className="bertioga-gallery reveal">
              <div className="gallery-main">
                <img
                  src={asset("bertioga-team.png")}
                  alt="Equipe com certificados do projeto educacional de Bertioga"
                  loading="lazy"
                  decoding="async"
                />
                <span>Reconhecimento e projeção internacional</span>
              </div>

              <div className="gallery-side">
                <img
                  src={asset("bertioga-award.png")}
                  alt="Troféu e certificado de Município Referência Google for Education de Bertioga"
                  loading="lazy"
                  decoding="async"
                />
                <div className="recognition-card">
                  <span>Resultado</span>
                  <strong>
                    Município Referência Google for Education no Brasil
                  </strong>
                  <p>
                    Um projeto que ganhou visibilidade pelo uso da tecnologia na
                    educação e pela experiência de alfabetização digital indígena.
                  </p>
                </div>
              </div>
            </div>

            <div className="case-evidence reveal">
              {[
                "Formação continuada de educadores",
                "Google for Education e Gemini",
                "Programa Aluno Tutor implantado",
                "Primeira turma de Alunos Tutores indígenas",
                "Fortalecimento da alfabetização e da cidadania digital",
                "Reconhecimento como Município Referência Google for Education",
                "Visibilidade internacional",
              ].map((t) => (
                <div key={t}>
                  <span>✓</span>
                  <p>{t}</p>
                </div>
              ))}
            </div>

            <div className="video-feature reveal">
              <button
                className="video-feature-media"
                type="button"
                onClick={() => setVideoOpen(true)}
                aria-label="Assistir ao vídeo do Google sobre o projeto"
              >
                <img
                  src={asset("aluno-tutor-indigena.png")}
                  alt="Estudante indígena utilizando um Chromebook durante o projeto em Bertioga"
                  loading="lazy"
                  decoding="async"
                />
                <span className="video-feature-play">
                  <span className="play-icon">▶</span>
                  <small>Assistir</small>
                </span>
              </button>

              <div className="video-feature-copy">
                <span className="eyebrow eyebrow-light">
                  Uma história real de transformação
                </span>
                <h2>Veja o vídeo do Google sobre o projeto realizado pela Calriz</h2>
                <p>
                  Conheça como tecnologia, educação e cultura se conectaram para
                  ampliar oportunidades e fortalecer o protagonismo dos estudantes
                  em Bertioga.
                </p>
                <button
                  className="video-feature-btn"
                  type="button"
                  onClick={() => setVideoOpen(true)}
                >
                  Assistir ao vídeo <span aria-hidden="true">→</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="section rnp-section">
          <div className="rnp-glow"></div>
          <div className="brand-shapes rnp-shapes" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>

          <div className="pp-container rnp-grid">
            <div className="rnp-copy reveal">
              <span className="eyebrow eyebrow-light">
                Formação em escala nacional
              </span>
              <h2>
                RNP: formação em tecnologia e inteligência artificial com alcance
                de milhões de pessoas
              </h2>
              <p>
                Em parceria com a Rede Nacional de Ensino e Pesquisa, a Calriz
                desenvolveu uma jornada formativa sobre o ecossistema Google e o
                uso da inteligência artificial no Ensino Superior.
              </p>
              <div className="rnp-callout">
                <span>Instituições alcançadas</span>
                <strong>UFMS · UFVJM · IFSertãoPE</strong>
                <p>
                  A iniciativa alcançou instituições de diferentes regiões do
                  país.
                </p>
              </div>
              <p>
                O conteúdo foi estruturado para proporcionar flexibilidade,
                aplicação prática e escala, permitindo que professores e
                profissionais das instituições conectadas à RNP avançassem no seu
                próprio ritmo e incorporassem os recursos à rotina acadêmica.
              </p>
            </div>

            <div className="rnp-numbers">
              <article className="rnp-number reveal">
                <CountUp end={4} suffix=" mi" />
                <p>alunos, professores e pesquisadores alcançados</p>
              </article>
              <article className="rnp-number reveal">
                <CountUp end={1831} />
                <p>universidades impactadas</p>
              </article>
              <article className="rnp-number reveal">
                <CountUp end={20} suffix="h" />
                <p>de formação assíncrona</p>
              </article>
            </div>
          </div>
        </section>

        <section className="section faeterj-section">
          <div className="pp-container">
            <div className="faeterj-label-row reveal">
              <span className="eyebrow">
                Transformação digital no Ensino Superior
              </span>
              <span className="faeterj-index">FAETERJ-Rio</span>
            </div>

            <div className="faeterj-grid">
              <div className="faeterj-copy reveal">
                
                <h2>
                  FAETERJ-Rio: tecnologia e formação para transformar a
                  experiência acadêmica
                </h2>
                <p>
                  A Calriz apoiou a FAETERJ-Rio em uma jornada de transformação
                  digital que integrou o Google Workspace for Education,
                  modernização tecnológica e capacitação de professores e equipes
                  acadêmicas.
                </p>
                <p>
                  A iniciativa preparou a instituição para novas práticas de
                  ensino presenciais, remotas e híbridas, ampliando a colaboração
                  e o uso pedagógico das ferramentas digitais.
                </p>
                <div className="faeterj-quote">
                  <strong>
                    Da infraestrutura à formação docente, uma transformação
                    completa para novas formas de ensinar e aprender.
                  </strong>
                </div>
              </div>

              <div className="faeterj-highlights reveal">
                <span className="faeterj-highlights-title">
                  Destaques do projeto
                </span>
                <div className="faeterj-highlight-grid">
                  {[
                    ["01", "Implantação do Google Workspace for Education"],
                    ["02", "Migração dos e-mails e integração de acessos"],
                    ["03", "Capacitação de professores e multiplicadores"],
                    ["04", "Estruturação do Google Classroom"],
                    ["05", "Fornecimento de Chromebooks e licenças"],
                    ["06", "Preparação para a certificação Google Educator Nível 1"],
                  ].map(([n, text]) => (
                    <article className="faeterj-highlight" key={n}>
                      <span>{n}</span>
                      <p>{text}</p>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section other-cases-section" id="experiencias">
          <div className="pp-container">
            <div className="other-cases-grid">
              <article className="other-case featured reveal">
                <img
                  src={asset("moodle-unesp-natural.png")}
                  alt="Profissionais participando de uma jornada de aprendizagem digital"
                  loading="lazy"
                  decoding="async"
                />
                <div className="other-case-content">
                  <div className="case-card-top">
                    <span>Ensino Superior</span>
                  </div>
                  <h3>UNESP</h3>
                  <h4>Formação em Desenvolvimento de Pessoas com recursos digitais</h4>
                  <p>
                    A equipe pedagógica da Calriz atua na construção do curso “A
                    Jornada de Navegação do Desenvolvimento de Pessoas”,
                    desenvolvido no Moodle para servidores e gestores da área de
                    Gestão de Pessoas da UNESP. Com 40 horas de formação, a jornada
                    foi organizada em formato de superpage, reunindo conteúdos,
                    fóruns, materiais de apoio criados no ecossistema do Google
                    Workspace e uma navegação contínua. A experiência aborda
                    diagnóstico de necessidades, planejamento, implementação e
                    avaliação de ações de desenvolvimento de pessoas.
                  </p>
                </div>
              </article>

              {[
                ["Rede Estadual", "02", "Secretaria do Estado da Educação - GO", "Formação de multiplicadores", "Implementação de uma jornada de formação para preparar professores da Rede Estadual de Educação de Goiás para disseminar o uso pedagógico das tecnologias Google e liderar a transformação digital em suas comunidades escolares."],
                ["Rede Municipal", "03", "Guaratinguetá", "Inteligência artificial na prática", "Oficinas práticas e imersivas sobre o uso do Gemini no contexto educacional, conectando inteligência artificial, planejamento docente e situações reais da sala de aula."],
                ["Rede Municipal", "04", "Itapeva", "Formação continuada", "Programa voltado à integração de ferramentas digitais ao cotidiano escolar, ampliando as competências dos educadores e apoiando novas práticas pedagógicas."],
                ["Rede Municipal", "05", "Mogi das Cruzes", "Competências digitais", "Capacitação docente para desenvolver competências digitais, estimular metodologias ativas e fortalecer o uso intencional da tecnologia na aprendizagem."],
              ].map(([tag, n, h3, h4, text]) => (
                <article className="other-case reveal" key={n}>
                  <div className="other-case-content">
                    <div className="case-card-top">
                      <span>{tag}</span>
                    </div>
                    <h3>{h3}</h3>
                    <h4>{h4}</h4>
                    <p>{text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="contact-section" id="contato">
          <div className="pp-container contact-grid">
            <div className="contact-copy reveal">
              <span className="eyebrow eyebrow-light">Vamos conversar?</span>
              <h2>Leve a transformação digital da estratégia para a prática</h2>
              <p>
                Conte com a Calriz para desenvolver uma jornada de formação
                conectada aos desafios, às pessoas e aos objetivos da sua
                instituição.
              </p>
            </div>

            <form className="contact-form reveal" id="contact-form" onSubmit={handleSubmit}>
              <div className="form-fields">
                <div className="form-heading">
                  <span>Fale com nosso time de Educação</span>
                  <strong>Conte um pouco sobre sua instituição</strong>
                </div>

                <label>
                  Nome
                  <input
                    type="text"
                    name="name"
                    placeholder="Seu nome"
                    autoComplete="name"
                    enterKeyHint="next"
                    required
                  />
                </label>

                <div className="form-row">
                  <label>
                    E-mail
                    <input
                      type="email"
                      name="email"
                      placeholder="nome@instituicao.br"
                      autoComplete="email"
                      inputMode="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="next"
                      required
                    />
                  </label>
                  <label>
                    Instituição
                    <input
                      type="text"
                      name="institution"
                      placeholder="Nome da instituição"
                      autoComplete="organization"
                      enterKeyHint="next"
                      required
                    />
                  </label>
                </div>

                <label>
                  Tenho interesse em
                  <select name="interest">
                    <option>Formação pedagógica</option>
                    <option>Programa Aluno Tutor</option>
                    <option>Google for Education e Gemini</option>
                    <option>Moodle e jornadas de aprendizagem</option>
                  </select>
                </label>

                <label>
                  Mensagem
                  <textarea
                    name="message"
                    placeholder="Como podemos apoiar sua instituição?"
                    rows={4}
                    enterKeyHint="enter"
                  ></textarea>
                </label>

                <ArrowButton type="submit" variant="pink" size="md" className="form-button" disabled={submitting}>
                  {submitting ? "Enviando..." : "Converse com nosso time pedagógico"}
                </ArrowButton>

                {status === "success" && (
                  <p className="form-feedback form-feedback-success">
                    Mensagem enviada com sucesso! Em breve entraremos em contato.
                  </p>
                )}
                {status === "error" && (
                  <p className="form-feedback form-feedback-error">
                    Erro ao enviar. Tente novamente em instantes.
                  </p>
                )}
              </div>
            </form>
          </div>
        </section>

        <footer>
          <div className="pp-container footer-top">
            <Link to="/" aria-label="Calriz - site principal">
              <img
                src={logoOficial}
                alt="Calriz"
                loading="lazy"
                decoding="async"
              />
            </Link>
            <p>Tecnologia que se transforma em prática, impacto e futuro.</p>
            <a href="#inicio">Voltar ao topo ↑</a>
          </div>
          <div className="pp-container footer-bottom">
            <span>© 2026 Calriz. Todos os direitos reservados.</span>
            <span>Parceiro Pedagógico · Educação 4.0</span>
          </div>
        </footer>

        <a
          className={`floating-contact${active === "contato" ? " is-hidden" : ""}`}
          href="#contato"
          aria-label="Fale conosco"
          tabIndex={active === "contato" ? -1 : undefined}
        >
          <span>Fale com nosso time</span>
          <i>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              className="right-arrow"
            >
              <path
                d="M5 12H19M13 6L19 12L13 18"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </i>
        </a>

        {videoOpen && (
          <div
            className="video-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Vídeo do case Bertioga"
            onClick={(e) => e.target === e.currentTarget && setVideoOpen(false)}
          >
            <div className="video-dialog">
              <div className="video-dialog-header">
                <div>
                  <span>Case Bertioga</span>
                  <strong>Projeto realizado pela Calriz</strong>
                </div>
                <button
                  type="button"
                  aria-label="Fechar vídeo"
                  onClick={() => setVideoOpen(false)}
                >
                  ×
                </button>
              </div>
              <div className="video-frame">
                <iframe
                  title="Case Bertioga realizado pela Calriz"
                  src="https://www.youtube.com/embed/FYYD_9Z2ACU?autoplay=1&rel=0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
